import api from '../../utils/api';

export interface SupportFaq {
  id: number;
  category: string;
  question_en: string;
  question_hi?: string | null;
  question_mr?: string | null;
  answer_en: string;
  answer_hi?: string | null;
  answer_mr?: string | null;
  action_type?: string | null;
  action_target?: string | null;
  action_label?: string | null;
  display_order: number;
  is_active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupportFaqResponse {
  success: boolean;
  count?: number;
  data: SupportFaq[];
}

export interface SingleSupportFaqResponse {
  success: boolean;
  data: SupportFaq;
}

export interface SupportFaqImportResponse {
  success: boolean;
  message: string;
  count: number;
  data?: SupportFaq[];
}

// 1. Fetch all FAQs
export const fetchFaqs = async (): Promise<SupportFaqResponse> => {
  try {
    const response = await api.get<SupportFaqResponse>('/master-admin/support-faq');
    return response.data;
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    throw error;
  }
};

// 2. Fetch FAQ by ID
export const fetchFaqById = async (id: number): Promise<SingleSupportFaqResponse> => {
  try {
    const response = await api.get<SingleSupportFaqResponse>(`/master-admin/support-faq/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching FAQ with ID ${id}:`, error);
    throw error;
  }
};

// 3. Create a new FAQ
export const createFaq = async (faqData: Omit<SupportFaq, 'id' | 'createdAt' | 'updatedAt'>): Promise<SingleSupportFaqResponse> => {
  try {
    const response = await api.post<SingleSupportFaqResponse>('/master-admin/support-faq', faqData);
    return response.data;
  } catch (error) {
    console.error('Error creating FAQ:', error);
    throw error;
  }
};

// 4. Update an existing FAQ
export const updateFaq = async (id: number, faqData: Partial<SupportFaq>): Promise<SingleSupportFaqResponse> => {
  try {
    const response = await api.put<SingleSupportFaqResponse>(`/master-admin/support-faq/${id}`, faqData);
    return response.data;
  } catch (error) {
    console.error(`Error updating FAQ with ID ${id}:`, error);
    throw error;
  }
};

// 5. Delete an FAQ
export const deleteFaq = async (id: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete<{ success: boolean; message: string }>(`/master-admin/support-faq/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting FAQ with ID ${id}:`, error);
    throw error;
  }
};

// 6. Import FAQs from Excel
export const importFaqs = async (file: File, mode: 'append' | 'replace'): Promise<SupportFaqImportResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);

    const response = await api.post<SupportFaqImportResponse>('/master-admin/support-faq/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error importing FAQs from Excel:', error);
    throw error;
  }
};
